export const COMPANY = {
  name: 'Black Vanilla Technologies',
  short: 'Black Vanilla',
  domain: 'blackvanilla.ai',
  email: 'contactus@blackvanilla.ai',
  productUrl: 'https://civil3d.app',
  tagline: 'Building Physical AI systems that understand the real world.',
  positioning:
    'Black Vanilla Technologies is a Physical AI and Spatial Intelligence product company. We own a reusable Perception + Spatial Intelligence stack. civil3d is the flagship product.',
};

export const NAV = [
  { path: '/technology', label: 'Technology' },
  { path: '/civil3d', label: 'civil3d' },
  { path: '/capabilities', label: 'Capabilities' },
  { path: '/company', label: 'Company' },
];

export const HERO = {
  eyebrow: 'Physical AI + Spatial Intelligence',
  title: 'We build the perception layer for the physical world.',
  support:
    'From sensors to spatial intelligence, Black Vanilla builds systems that allow machines and software to perceive, reconstruct and understand real environments.',
  primaryCta: { label: 'Explore civil3d', to: '/civil3d' },
  secondaryCta: { label: 'Explore our technology', to: '/technology' },
  builtWith: 'Jetson · ROS 2 · NVIDIA accelerated computing · LiDAR · Vision · IMU',
};

export const WHAT_WE_BUILD = {
  eyebrow: 'What we build',
  title: 'Physical intelligence, from sensing to spatial understanding.',
  items: [
    {
      step: '01',
      title: 'Perception & Spatial Intelligence',
      body: 'Multimodal sensing, localization, mapping, 3D reconstruction and semantic understanding of physical environments.',
    },
    {
      step: '02',
      title: 'Edge AI Engineering',
      body: 'Real-time perception and inference on embedded and accelerated platforms.',
    },
    {
      step: '03',
      title: 'Autonomous & Intelligent Systems',
      body: 'Perception systems that help robots and machines localize, understand surroundings and act safely.',
    },
  ],
};

export const APPLIED = {
  eyebrow: 'Applied Physical AI',
  title: 'Intelligence that operates in the real world.',
  items: [
    {
      id: 'built',
      title: 'Built Environment',
      body: 'Turn real sites into explorable spatial models for documentation, coordination and change.',
      to: '/civil3d',
      asset: 'built-environment',
    },
    {
      id: 'robotics',
      title: 'Robotics',
      body: 'Give machines the localization, mapping and scene understanding required to move through unstructured space.',
      to: '/capabilities',
      asset: 'robotics-demo',
    },
    {
      id: 'industrial',
      title: 'Industrial',
      body: 'Bring perception to industrial hardware for inspection, monitoring and time-critical decisions.',
      to: '/capabilities',
      asset: 'industrial-vision',
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      body: 'Capture and reconstruct bridges, corridors and outdoor works as measurable spatial records.',
      to: '/civil3d',
      asset: 'infrastructure',
    },
  ],
};

export const LOOP = [
  {
    id: 'sense',
    step: '01',
    title: 'Sense',
    body: 'LiDAR, cameras, IMU, and depth capture the geometry and motion of a real place.',
  },
  {
    id: 'understand',
    step: '02',
    title: 'Understand',
    body: 'Perception reconstructs space, localizes within it, and assigns structure and meaning.',
  },
  {
    id: 'act',
    step: '03',
    title: 'Act',
    body: 'Products use that spatial understanding — for the built environment, robots, and industrial systems.',
  },
];

export const STACK = {
  sensors: ['LiDAR', 'Vision', 'IMU', 'Stereo / Depth'],
  core: 'Black Vanilla Perception',
  outputs: ['Localization', 'Mapping', 'Reconstruction', 'Semantics'],
  applications: ['civil3d', 'Robotic autonomy', 'Industrial vision'],
};

export const CIVIL3D = {
  eyebrow: 'Flagship product',
  name: 'civil3d',
  tagline: 'Spatial intelligence for the built environment.',
  body:
    'civil3d applies Black Vanilla’s perception stack to buildings, sites, and infrastructure. The product is in active development. Current work focuses on capturing real spaces and turning them into measurable 3D environments that support as-built understanding and progress review.',
  process: ['Capture', 'Reconstruct', 'Understand'],
  homeOutcomes: [
    'Existing-condition capture',
    'Measurable 3D environments',
    'As-built documentation',
    'Progress and change intelligence',
  ],
  outcomes: [
    {
      title: 'Spatial capture',
      body: 'Record real environments with the sensors on hand — then bring that capture into a reconstruction pipeline.',
    },
    {
      title: 'Measurable 3D environments',
      body: 'Reconstruct spaces you can inspect, navigate, and measure, instead of relying only on drawings or photos.',
    },
    {
      title: 'As-built intelligence',
      body: 'See existing conditions as they are, so design, construction, and operations share one spatial picture.',
    },
    {
      title: 'Progress and deviation',
      body: 'Compare captures over time as the product matures — to review change, not just a single snapshot.',
    },
  ],
  note:
    'civil3d is research and product development in progress. Capabilities below describe the direction of the product, not a finished production SLA.',
};

export const CAPABILITIES = [
  {
    id: 'spatial',
    title: 'Spatial Intelligence & Digital Twins',
    body:
      'Deploy perception and reconstruction into the built environment: capture, model, and reason about sites, interiors, and infrastructure as they exist today.',
    tags: ['Capture', 'Reconstruction', 'As-built', 'Digital twins'],
  },
  {
    id: 'robotics',
    title: 'Robotic Perception & Autonomy',
    body:
      'Give machines a spatial model of the world they move through — localization, mapping, and scene understanding on the same stack that powers civil3d.',
    tags: ['Localization', 'Mapping', 'Scene understanding', 'On-robot perception'],
  },
  {
    id: 'industrial',
    title: 'Industrial Vision & Edge AI',
    body:
      'Run perception at the edge where latency, connectivity, and physical process matter — inspection, monitoring, and spatial sensing on industrial hardware.',
    tags: ['Edge compute', 'Vision', 'LiDAR', 'On-device inference'],
  },
];

export const TECHNOLOGY = {
  eyebrow: 'Technology',
  title: 'A reusable perception stack.',
  lead:
    'Black Vanilla builds multimodal perception once, then applies it across products. civil3d is the first; robotics and industrial vision share the same core.',
  modalities: [
    {
      title: 'LiDAR',
      body: 'Range and structure from active sensing — geometry that does not depend on texture or lighting alone.',
    },
    {
      title: 'RGB',
      body: 'Appearance, materials, and visual context from cameras, fused with geometry rather than used in isolation.',
    },
    {
      title: 'IMU',
      body: 'Motion and orientation that stabilize localization when cameras or LiDAR are briefly uncertain.',
    },
    {
      title: 'Stereo / depth',
      body: 'Passive depth where a LiDAR is not the right sensor, or as a complementary channel at close range.',
    },
    {
      title: 'Edge compute',
      body: 'On-device perception for capture, localization, and time-critical loops — without waiting on a round trip.',
    },
    {
      title: 'Cloud reconstruction',
      body: 'Heavier mapping, reconstruction, and spatial reasoning where compute and collaboration belong off-device.',
    },
  ],
  reasoning:
    'Spatial reasoning sits above reconstruction: not only a mesh or a point cloud, but a model of place that products can query, compare, and act on.',
};

export const COMPANY_PAGE = {
  eyebrow: 'Company',
  title: 'Black Vanilla Technologies',
  lead: 'Physical AI / Spatial Intelligence.',
  body: [
    'We build systems that understand the real world — not a generic software services practice.',
    'The company owns a Perception + Spatial Intelligence stack: sense, reconstruct, localize, and assign meaning. civil3d is how that stack meets the built environment. The same core is intended for robotic perception and industrial vision.',
    'If you are deploying spatial intelligence into a physical operation, we want to talk. Engagements are about putting this technology to work — not staff augmentation.',
  ],
};
